import React, {useEffect, useState} from 'react'
import {Modal, Button, Form, Row, Col} from 'react-bootstrap'
import {MultiSelectWithActions} from 'app/tfi/components/MultiSelectWithActions'
import {RecipientFilters} from '../recipientsTypes'

interface StateOption {
  id: string
  name: string
  code: string
}

interface CarrierOption {
  id: string
  name: string
}

interface PolicyStatusOption {
  value: string
  label: string
}

interface RecipientAdvancedFiltersModalProps {
  show: boolean
  onHide: () => void
  filters: RecipientFilters
  onApply: (filters: Partial<RecipientFilters>) => void
  states?: StateOption[]
  carriers?: CarrierOption[]
  policyStatusOptions?: PolicyStatusOption[]
}

const EMPTY_ADVANCED_FILTERS: Partial<RecipientFilters> = {
  state: [],
  carrier: [],
  policyStatus: '',
  isPolicyHolder: undefined,
  withoutPolicy: undefined,
}

export const RecipientAdvancedFiltersModal: React.FC<RecipientAdvancedFiltersModalProps> = ({
  show,
  onHide,
  filters,
  onApply,
  states = [],
  carriers = [],
  policyStatusOptions = [],
}) => {
  const [pendingFilters, setPendingFilters] = useState<Partial<RecipientFilters>>(EMPTY_ADVANCED_FILTERS)

  useEffect(() => {
    if (!show) return
    setPendingFilters({
      state: filters.state,
      carrier: filters.carrier,
      policyStatus: filters.policyStatus,
      isPolicyHolder: filters.isPolicyHolder,
      withoutPolicy: filters.withoutPolicy,
    })
  }, [show, filters])

  const stateOptions = states.map((state) => ({value: state.code, label: `${state.name} (${state.code})`}))
  const selectedStateOptions = stateOptions.filter((option) => (pendingFilters.state || []).includes(option.value))
  const carrierOptions = carriers.map((carrier) => ({value: carrier.id, label: carrier.name}))
  const selectedCarrierOptions = carrierOptions.filter((option) => (pendingFilters.carrier || []).includes(String(option.value)))
  const policyMembershipValue =
    pendingFilters.withoutPolicy === true
      ? 'no_policy'
      : pendingFilters.isPolicyHolder === true
        ? 'holder'
        : pendingFilters.isPolicyHolder === false && pendingFilters.withoutPolicy === false
          ? 'family'
          : ''

  const handleApply = () => {
    onApply(pendingFilters)
    onHide()
  }

  const handleClear = () => {
    setPendingFilters(EMPTY_ADVANCED_FILTERS)
  }

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>Advanced Filters</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='g-3'>
          <Col md={6}>
            <Form.Label className='small fw-semibold'>State</Form.Label>
            <MultiSelectWithActions
              value={selectedStateOptions}
              options={stateOptions}
              onChange={(selected: any) =>
                setPendingFilters((prev) => ({
                  ...prev,
                  state: selected ? selected.map((option: any) => option.value) : [],
                }))
              }
            />
          </Col>
          <Col md={6}>
            <Form.Label className='small fw-semibold'>Carrier</Form.Label>
            <MultiSelectWithActions
              value={selectedCarrierOptions}
              options={carrierOptions}
              onChange={(selected: any) =>
                setPendingFilters((prev) => ({
                  ...prev,
                  carrier: selected ? selected.map((option: any) => option.value) : [],
                }))
              }
            />
          </Col>
          <Col md={6}>
            <Form.Label className='small fw-semibold'>Policy status</Form.Label>
            <Form.Select
              size='sm'
              value={pendingFilters.policyStatus ?? ''}
              onChange={(e) => setPendingFilters((prev) => ({...prev, policyStatus: e.target.value}))}
            >
              <option value=''>Any status</option>
              {policyStatusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label className='small fw-semibold'>Policy relationship</Form.Label>
            <Form.Select
              size='sm'
              value={policyMembershipValue}
              onChange={(e) =>
                setPendingFilters((prev) => ({
                  ...prev,
                  isPolicyHolder:
                    e.target.value === 'holder' ? true : e.target.value === 'family' ? false : undefined,
                  withoutPolicy:
                    e.target.value === 'no_policy' ? true : e.target.value === 'holder' || e.target.value === 'family' ? false : undefined,
                }))
              }
            >
              <option value=''>All contacts</option>
              <option value='holder'>Policy holders</option>
              <option value='family'>Family members</option>
              <option value='no_policy'>No policy</option>
            </Form.Select>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={handleClear}>Clear</Button>
        <Button variant='secondary' onClick={onHide}>Cancel</Button>
        <Button variant='primary' onClick={handleApply}>Apply filters</Button>
      </Modal.Footer>
    </Modal>
  )
}
